import React from 'react';

import { PropertyType } from '../types';
import { Section, SectionBody, SectionTitle, SecondaryTitle, PropertyTypeWrapper } from '../styled/common';
import { PerformantCheckboxGroup } from '../common/PerformantCheckbox';
import FilterSection from '../common/DropdownSection';
import { isFilterActiveByType } from '../utils';
import { useLocale } from 'locale';
import { GenericFilterProps } from '../types';
import { head, without } from 'lodash';
import { CheckboxChip, CheckboxChipGroup } from './amenities/checkbox-chip/CheckboxChip';
import { useScreenBreakpoint } from 'consts/breakpoints';
import FlatIcon from 'assets/svg/combined-filters-search-view/Flat.svg';
import PrivateHouseIcon from 'assets/svg/combined-filters-search-view/PrivateHouse.svg';
import GardenApartmentIcon from 'assets/svg/combined-filters-search-view/GardenApartment.svg';
import CottageIcon from 'assets/svg/combined-filters-search-view/Cottage.svg';
import TwoFamilyIcon from 'assets/svg/combined-filters-search-view/TwoFamily.svg';
import PenthouseIcon from 'assets/svg/combined-filters-search-view/Penthouse.svg';
import RooftopIcon from 'assets/svg/combined-filters-search-view/Rooftop.svg';
import FarmlandIcon from 'assets/svg/combined-filters-search-view/Farmland.svg';
import DuplexIcon from 'assets/svg/combined-filters-search-view/Duplex.svg';
import LandIcon from 'assets/svg/combined-filters-search-view/Land.svg';
import BuildingIcon from 'assets/svg/combined-filters-search-view/Building.svg';
import OfficeIcon from 'assets/svg/combined-filters-search-view/Office.svg';
import ShopIcon from 'assets/svg/combined-filters-search-view/Shop.svg';
import SharedWorkSpaceIcon from 'assets/svg/combined-filters-search-view/SharedWorkSpace.svg';
import StudioIcon from 'assets/svg/combined-filters-search-view/Studio.svg';
import HallIcon from 'assets/svg/combined-filters-search-view/Hall.svg';
import ClinicIcon from 'assets/svg/combined-filters-search-view/Clinic.svg';
import RestaurantIcon from 'assets/svg/combined-filters-search-view/Restaurant.svg';
import HotelIcon from 'assets/svg/combined-filters-search-view/Hotel.svg';
import IndustrialBuildingIcon from 'assets/svg/combined-filters-search-view/IndustrialBuilding.svg';
import StoreroomIcon from 'assets/svg/combined-filters-search-view/Warehouse.svg';
import BasementIcon from 'assets/svg/combined-filters-search-view/Basement.svg';
import ParkingLotIcon from 'assets/svg/combined-filters-search-view/ParkingLot.svg';
import IncomeProducingPropertyIcon from 'assets/svg/combined-filters-search-view/IncomeProducingProperty.svg';
import AgricultureLandIcon from 'assets/svg/combined-filters-search-view/AgricultureLand.svg';

const combinedFiltersSearchPropertyTypeToIcon: Partial<Record<PropertyType, React.ReactNode>> = {
  [PropertyType.Flat]: <FlatIcon />,
  [PropertyType.PrivateHouse]: <PrivateHouseIcon />,
  [PropertyType.GardenApartment]: <GardenApartmentIcon />,
  [PropertyType.Cottage]: <CottageIcon />,
  [PropertyType.TwoFamilyDwelling]: <TwoFamilyIcon />,
  [PropertyType.Penthouse]: <PenthouseIcon />,
  [PropertyType.Rooftop]: <RooftopIcon />,
  [PropertyType.FarmLand]: <FarmlandIcon />,
  [PropertyType.Duplex]: <DuplexIcon />,
  [PropertyType.Land]: <LandIcon />,
  [PropertyType.Building]: <BuildingIcon />,
  [PropertyType.Office]: <OfficeIcon />,
  [PropertyType.Shop]: <ShopIcon />,
  [PropertyType.SharedWorkSpace]: <SharedWorkSpaceIcon />,
  [PropertyType.Studio]: <StudioIcon />,
  [PropertyType.Hall]: <HallIcon />,
  [PropertyType.Clinic]: <ClinicIcon />,
  [PropertyType.Restaurant]: <RestaurantIcon />,
  [PropertyType.Hotel]: <HotelIcon />,
  [PropertyType.IndustrialBuilding]: <IndustrialBuildingIcon />,
  [PropertyType.Storeroom]: <StoreroomIcon />,
  [PropertyType.Basement]: <BasementIcon />,
  [PropertyType.ParkingLot]: <ParkingLotIcon />,
  [PropertyType.IncomeProducingProperty]: <IncomeProducingPropertyIcon />,
  [PropertyType.AgricultureLand]: <AgricultureLandIcon />,
};

interface PropertyTypesProps extends GenericFilterProps<'propertyTypes'> {}

const PropertyTypes: React.FC<PropertyTypesProps> = props => {
  const { t } = useLocale();
  const {
    intermediateValue,
    isMobile,
    isInline,
    initialValue,
    setIntermediateValue,
    resetValue,
    commitValues,
    commitedValue,
    type,
    config,
    marketplace,
    alignment,
  } = props;

  const isCombinedFiltersSearchView = useScreenBreakpoint() < 3;

  const checkboxLabelGetter = (value: PropertyType) => (
    t('filters.propertyTypeOptions', { value, marketPlace: marketplace })
  );

  const { propertyTypes, filtersIcons } = config;

  const checkboxGroup = (
    <PerformantCheckboxGroup
      values={intermediateValue}
      options={propertyTypes}
      labelGetter={checkboxLabelGetter}
      valueSetter={setIntermediateValue}
      mode={isCombinedFiltersSearchView ? 'black' : 'default'}
    />
  );

  if (isInline) {
    return (
      <Section combinedFiltersSearchMode={isCombinedFiltersSearchView} data-section-filter-name={type} data-auto={`filter-${type}`}>
        <SectionTitle combinedFiltersSearchMode={isCombinedFiltersSearchView} isBold={isMobile}>
          <div data-auto="propertyTypesTitle">
            {t('filters.propertyType', { count: null })}
            {!isCombinedFiltersSearchView && intermediateValue.length ? (
              <SecondaryTitle data-auto="selectedTypesCount">
                {intermediateValue.length}
              </SecondaryTitle>
            ) : null}
          </div>
        </SectionTitle >
        <SectionBody combinedFiltersSearchMode={isCombinedFiltersSearchView} data-auto="filterCheckboxes">
          {isCombinedFiltersSearchView ? (
            <CheckboxChipGroup>
              {propertyTypes.map(item => (
                <CheckboxChip
                  key={item}
                  label={checkboxLabelGetter(item)}
                  icon={combinedFiltersSearchPropertyTypeToIcon[item]}
                  checked={intermediateValue.includes(item)}
                  onClick={() => setIntermediateValue(intermediateValue.includes(item)
                    ? without(intermediateValue, item)
                    : intermediateValue.concat(item))}
                />
              ))}
            </CheckboxChipGroup>
          ) : (
            <PropertyTypeWrapper isInline={!isMobile}>
              {checkboxGroup}
            </PropertyTypeWrapper>
          )}
        </SectionBody>
      </Section>
    );
  }

  return (
    <FilterSection
      isClearButtonDisabled={!isFilterActiveByType.propertyTypes(intermediateValue, initialValue)}
      type={type}
      alignment={alignment}
      icon={filtersIcons[type]}
      onClear={resetValue}
      label={t('filters.propertyType', { count: commitedValue.length, value: checkboxLabelGetter(head(commitedValue)) })}
      onSubmit={commitValues}
      isActive={isFilterActiveByType.propertyTypes(commitedValue, initialValue)}
    >
      {checkboxGroup}
    </FilterSection>
  );
};

export default PropertyTypes;
